import { registerPlugin } from "@capacitor/core";

export type GlobalDictationStatus = {
  enabled: boolean;
  microphoneGranted: boolean;
  overlayGranted: boolean;
  accessibilityGranted: boolean;
  batteryOptimizationIgnored: boolean;
  setupComplete: boolean;
  configured?: boolean;
  overlayVisible?: boolean;
  recording?: boolean;
  transcribing?: boolean;
  lastRecordingPath?: string;
  lastRecordingDurationMs?: number;
  lastErrorMessage?: string;
  keyboardEnabled?: boolean;
  fullAccessGranted?: boolean;
  paywallRequired?: boolean;
  // Android: the resting edge handle is drawn fainter.
  bubbleFaint?: boolean;
  flowSessionActive?: boolean;
  flowSessionExpiresAt?: number;
  flowSessionStarted?: boolean;
  frameLog?: string[];
};

export type GlobalDictationConfig = {
  authToken?: string;
  serverUrl?: string;
  // Android mirrors this preference only so its out-of-app bubble can render
  // and gate the picker. The transcription request remains audio-only.
  dictationLanguage?: string;
  recentDictationLanguages?: string[];
  debugFrames?: boolean;
  // Serialized custom find→replace entries from
  // composeDictationVocabularyRules. Applied by native right before insertion.
  vocabularyRules?: string;
};

export type GlobalDictationPlugin = {
  getStatus: () => Promise<GlobalDictationStatus>;
  configure: (options: GlobalDictationConfig) => Promise<GlobalDictationStatus>;
  setLocale: (options: { locale: string }) => Promise<void>;
  setEnabled: (options: { enabled: boolean }) => Promise<GlobalDictationStatus>;
  setBubbleAppearance: (options: {
    faint: boolean;
  }) => Promise<GlobalDictationStatus>;
  // Android: the bubble's last dictations, serialized [{ text, at }].
  getRecentDictations: () => Promise<{ items: string }>;
  requestMicrophonePermission: () => Promise<GlobalDictationStatus>;
  openOverlayPermissionSettings: () => Promise<void>;
  openAccessibilitySettings: () => Promise<void>;
  openBatteryOptimizationSettings: () => Promise<void>;
  openKeyboardSettings: () => Promise<void>;
  startFlowSession: () => Promise<GlobalDictationStatus>;
  stopFlowSession: () => Promise<GlobalDictationStatus>;
};

export const globalDictationPlugin =
  registerPlugin<GlobalDictationPlugin>("GlobalDictation");
